import {
  cloneDeep,
  get,
  groupBy,
  size,
  map,
  filter,
  find,
  isNumber,
  forEach,
  set,
  findIndex,
  isObject,
  isString,
  omit,
  sortBy,
  reduce,
  round,
} from 'lodash-es';
import uuidv4 from 'uuid/v4';

import { checkIsEmpty } from '../../Services/SalesRoutes/utils';
import { formatDate, formatAmountCurrency, formatPercent, getLabel, getNewTitle } from '../../Utils/Utils';
import { statusSalesRouteEnum } from '../../Services/SalesRoutes/enum';

const replaceCondition = (condition, currentItem) => {
  const newCondition = { ...condition };

  for (let key in newCondition) {
    const value = newCondition[key];

    if (isObject(value)) {
      newCondition[key] = replaceCondition(value, currentItem);
    } else if (isString(value)) {
      if (value.indexOf('__') >= 0) {
        newCondition[key] = get(currentItem, value.replace(/__/g, ''), value);
      }
    }
  }

  return newCondition;
};

const getPath = ({ objectFound, indexFinded, question, oldPath, name, findPath, currentItem }) => {
  const newFindPath = `${findPath}.find`;
  let newPath;

  if (oldPath) {
    newPath = `${oldPath}.${name}[${indexFinded}]`;
  } else {
    newPath = `${name}[${indexFinded}]`;
  }

  if (get(question, newFindPath)) {
    return getDeepPathFromAnswer({
      question,
      findObject: objectFound,
      findPath: newFindPath,
      oldPath: newPath,
      currentItem,
    });
  }

  return [newPath];
};

// get deep path from answer
const getDeepPathFromAnswer = ({ question, findObject, findPath = 'path', oldPath, currentItem }) => {
  const name = oldPath ? get(question, `${findPath}.name`) : get(question, `${findPath}.root`);
  const condition = get(question, `${findPath}.condition`);

  if (condition) {
    let conditionFilled = condition;

    if (condition.indexOf('__') >= 0) {
      conditionFilled = replaceCondition(JSON.parse(conditionFilled), currentItem);
    } else {
      conditionFilled = JSON.parse(conditionFilled);
    }

    const objectFound = find(get(findObject, name), conditionFilled);
    const indexFinded = findIndex(get(findObject, name), conditionFilled);

    return getPath({ objectFound, indexFinded, question, oldPath, name, findPath, currentItem }).flat();
  }

  return map(get(findObject, name), (obj, idx) =>
    getPath({ objectFound: obj, indexFinded: idx, question, oldPath, name, findPath, currentItem })
  ).flat();
};

const getCompletePaths = (question, answers, currentItem) => {
  const paths = getDeepPathFromAnswer({ question, findObject: answers, currentItem });

  return paths ? paths.map(path => `${path}.${get(question, 'path.path')}`) : [`${get(question, 'path.path')}`];
};

/**
 * partialUpdateSalesRouteContract Transform
 */
export const partialUpdateSalesRouteContractTransform = ({ answers }) => {
  try {
    return { offers: map(answers, item => item.ref) };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`partialUpdateSalesRouteContractTransform ${e.stack}`);
  }
  return undefined;
};

/**
 * partialUpdateSalesRoutePodiums Transform
 * @param {*} param0
 */
export const partialUpdateSalesRoutePodiumsTransform = ({ selections, salesroute, questionnaire }) => {
  try {
    const parts = get(selections, 'parts');
    const ref = get(salesroute, 'questionnaires[0].ref');
    const id = get(questionnaire, 'id');

    if (checkIsEmpty(ref) || checkIsEmpty(id) || size(parts) === 0) {
      // eslint-disable-next-line no-console
      console.error(`partialUpdateSalesRoutePodiumsTransform: Error data Transform`);
      return undefined;
    }

    return {
      questionnaires: [
        {
          parts,
          ref,
          questionnaire: {
            id,
          },
        },
      ],
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`partialUpdateSalesRoutePodiumsTransform: ${e.stack}`);
  }
  return undefined;
};

const getAnswersWithPath = answers =>
  reduce(
    answers,
    (acc, answer) => {
      const path = answer?.path ? `${get(answer, 'path.path')}` : answer?.code;

      if (answer?.value != null && answer?.value !== '') {
        set(acc, path, answer?.value);
      }

      return acc;
    },
    {}
  );

/**
 * partialUpdateSalesRouteAnswers Transform
 * @param {*} param0
 */
export const partialUpdateSalesRouteAnswersTransform = ({ answers, salesroute, questionnaire }) => {
  try {
    const ref = get(salesroute, 'questionnaires[0].ref');
    const id = get(questionnaire, 'id');

    const parts = map(get(answers, 'parts', []), part => ({
      id: part.id,
      answers: getAnswersWithPath(part.answers),
    }));

    if (checkIsEmpty(ref) || checkIsEmpty(id) || size(parts) === 0) {
      // eslint-disable-next-line no-console
      console.error(`partialUpdateSalesRouteAnswersTransform: Error data Transform`);
      return undefined;
    }

    // Remove offer when updating quotations
    const quotations = map(salesroute.quotations, ({ offer, ...rest }) => ({ ...rest }));

    return {
      questionnaires: [
        {
          parts,
          ref,
          questionnaire: {
            id,
          },
        },
      ],
      quotations,
      contracts: null, // Remove contracts when updating questionnaire/answers
      status: statusSalesRouteEnum.CREATED,
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`partialUpdateSalesRouteAnswersTransform: ${e.stack}`);
  }
  return undefined;
};

/**
 * partialUpdateSalesRouteLoans Transform
 * @param {*} param0
 */
export const partialUpdateSalesRouteLoansTransform = ({ answers, salesroute }) => {
  try {
    const loansAnswers = {};
    const graduatedAnswers = {};
    const currentLoans = get(salesroute, 'loans', []);

    forEach(answers, (someAnswers, key) => {
      if (key.indexOf('graduated_') >= 0) {
        const keySplit = key.split('_');

        if (!graduatedAnswers[keySplit[1]]) {
          graduatedAnswers[keySplit[1]] = {};
        }

        graduatedAnswers[keySplit[1]][keySplit[2]] = someAnswers;
      } else {
        loansAnswers[key] = someAnswers;
      }
    });

    const loans = map(loansAnswers, (loanAsw, key) => {
      // Concatenate answers of all parts in the same object
      const partAnswers = reduce(
        get(loanAsw, 'parts', []),
        (acc, part) => ({
          ...acc,
          ...getAnswersWithPath(part.answers),
        }),
        {}
      );

      // HACK for percent field that need to be between 0 and 1
      if (partAnswers.rate != null) {
        partAnswers.rate = `${round(parseFloat(partAnswers.rate) / 100, 4)}`;
      }

      if (partAnswers.kind === 'graduated') {
        partAnswers.increments = map(get(graduatedAnswers, `[${key}]`, []), (graduatedAsw, key) => {
          const incr = reduce(
            get(graduatedAsw, 'parts', []),
            (acc, part) => ({
              ...acc,
              ...getAnswersWithPath(part.answers),
            }),
            {}
          );

          incr.id = key;

          return incr;
        });
      }

      const currentLoan = find(currentLoans, { ref: key }) || {};

      return {
        ...currentLoan,
        ...partAnswers,
        ref: key,
      };
    });

    // Remove offer when updating quotations
    const quotations = map(salesroute.quotations, ({ offer, ...rest }) => ({ ...rest }));

    return {
      loans: sortBy(loans, ['ref']),
      quotations,
      status: statusSalesRouteEnum.LOANSDESCRIBED,
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`partialUpdateSalesRouteLoansTransform: ${e.stack}`);
  }

  return undefined;
};

const getAnswersOrdered = (answers, salesroute) => {
  const answersOrdered = [];

  forEach(get(salesroute, 'quotation', []), quotation => {
    forEach(quotation?.covereds, cov => {
      if (get(answers, `[${cov.party.ref}]`)) {
        answersOrdered.push({ id: cov.party.ref, value: get(answers, `[${cov.party.ref}]`) });
      }
    });
  });

  forEach(answers, (blockAnswers, partieId) => {
    if (!find(answersOrdered, { id: partieId })) {
      answersOrdered.push({ id: partieId, value: blockAnswers });
    }
  });

  return answersOrdered;
};

/**
 * partialUpdateSalesRouteQuotations Transform
 * @param {*} param0
 */
export const partialUpdateSalesRouteQuotationsTransform = ({ answers, salesroute }) => {
  try {
    const questionnaire = get(salesroute, 'questionnaires[0]');
    let quotations = get(salesroute, 'quotations');

    const newParties = [];

    // Order to keep covereds order
    const answersOrdered = getAnswersOrdered(answers, salesroute);

    // Remove offer when updating quotations
    quotations = map(quotations, ({ offer, ...rest }) => ({
      ...rest,
      questionnaire: { ref: get(questionnaire, 'ref', rest.questionnaire.ref) },
    }));

    quotations = map(quotations, quotation => {
      const covSaved = [...quotation.covereds];
      const newQuotation = { ...quotation, covereds: [] };

      // Get quotation's covereds and parties
      forEach(answersOrdered, ({ id }) => {
        let covered = find(covSaved, { party: { ref: id } });

        if (!covered) {
          covered = cloneDeep(get(questionnaire, 'description.generic_blocks.covered'));
          covered.party.ref = id;
        }

        if (!covered) {
          console.error('Should not be here... Should have generic_blocks');

          if (covSaved[0]) {
            covered = omit(covSaved[0], 'loan_shares', 'extra_data');
            covered.coverages = map(covered.coverages, cov => ({ coverage: cov.coverage }));
          } else {
            covered = { party: {} };
          }

          covered.party.ref = id;
        }

        newQuotation.covereds.push(covered);

        let partie = find(newParties, { ref: covered.party.ref });

        if (partie) {
          return;
        }

        if (!partie) {
          partie = find(salesroute?.parties, { ref: covered.party.ref });
        }

        if (!partie) {
          partie = cloneDeep(get(questionnaire, 'description.generic_blocks.party'));
          partie.ref = id;
        }

        if (!partie) {
          console.error('Should not be here... Should have generic_blocks');
          partie = { ref: id, is_person: true };
        }

        newParties.push(partie);
      });

      if (quotation?.subscriber?.ref) {
        let subscr = find(newParties, { ref: quotation.subscriber.ref });

        if (!subscr) {
          subscr = find(salesroute?.parties, { ref: quotation.subscriber.ref });
          newParties.push(subscr);
        }
      }

      return newQuotation;
    });

    const newSR = {
      quotations,
      parties: newParties,
      contracts: null, // Remove contracts when simulating quotations
      status: statusSalesRouteEnum.COVEREDDESCRIBED,
    };

    forEach(quotations, quotation => {
      forEach(answersOrdered, ({ id, value }) => {
        const covered = find(quotation.covereds, { party: { ref: id } });

        if (!covered) {
          console.log('Why no covered? It should exists! (or we have multiple quotations)');
          return;
        }

        const allAnswers = map(get(value, 'parts', []), item => item?.answers).flat();

        forEach(allAnswers, answerQuot => {
          const completePaths = getCompletePaths(answerQuot, newSR, covered);

          forEach(completePaths, completePath => {
            if (answerQuot?.code !== 'relation') {
              set(newSR, completePath, answerQuot?.value);
            } else if (answerQuot?.value && id !== quotation.subscriber.ref) {
              // Fill relation with to and ref
              const newPath = completePath.replace(/\.type$/, '');

              set(newSR, completePath, answerQuot?.value);
              set(newSR, `${newPath}.to`, { ref: quotation.subscriber.ref });
              set(newSR, `${newPath}.ref`, `rel_${uuidv4()}`);
            }
          });
        });
      });
    });

    return newSR;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`partialUpdateSalesRouteQuotationsTransform: ${e.stack}`);
  }
  return undefined;
};

/**
 * partialUpdateSalesRouteExtraData Transform
 * @param {*} param0
 */
export const partialUpdateSalesRouteExtraDataTransform = ({ answers, salesroute, codeQuestionnaire }) => {
  try {
    const allAnswers = map(get(answers, 'parts', []), item => item?.answers).flat();
    let quotations = get(salesroute, 'quotations');

    // Remove offer when updating quotations
    quotations = map(quotations, ({ offer, ...rest }) => ({
      ...rest,
      questionnaire: { ref: get(salesroute, 'questionnaires[0].ref', rest.questionnaire.ref) },
    }));

    const newSR = {
      quotations,
      contracts: null, // Remove contracts when simulating quotations
      status: statusSalesRouteEnum.EXTRADATADESCRIBED,
    };

    forEach(quotations, quotation => {
      forEach(allAnswers, answer => {
        const completePaths = getCompletePaths(answer, newSR);

        forEach(completePaths, completePath => {
          set(newSR, completePath, answer?.value);
        });
      });
    });

    return newSR;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`partialUpdateSalesRouteExtraDataTransform: ${e.stack}`);
  }
  return undefined;
};

/**
 * partialUpdateSalesRouteGeneralData Transform
 * @param {*} param0
 */
export const partialUpdateSalesRouteGeneralDataTransform = ({ answers, salesroute, codeQuestionnaire }) => {
  try {
    const allAnswers = map(get(answers, 'parts', []), item => item?.answers).flat();
    let quotations = get(salesroute, 'quotations');

    // Remove offer when updating quotations
    quotations = map(quotations, ({ offer, ...rest }) => ({
      ...rest,
      questionnaire: { ref: get(salesroute, 'questionnaires[0].ref', rest.questionnaire.ref) },
    }));

    const newSR = {
      quotations,
      loans: get(salesroute, 'loans'),
      contracts: null, // Remove contracts when simulating quotations
      status: statusSalesRouteEnum.GENERALDATADESCRIBED,
    };

    forEach(quotations, quotation => {
      forEach(allAnswers, answer => {
        const completePaths = getCompletePaths(answer, newSR);

        forEach(completePaths, completePath => {
          set(newSR, completePath, answer?.value);
        });
      });
    });

    return newSR;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`partialUpdateSalesRouteGeneralDataTransform: ${e.stack}`);
  }
  return undefined;
};

/**
 * partialUpdateSalesRouteSubscription Transform
 */
export const partialUpdateSalesRouteSubscriptionTransform = ({ answers, salesroute, codeQuestionnaire }) => {
  try {
    const subscrData = map(answers, (subscrAnswers, key) => {
      // Group by path root and concatenate answers of all parts in the same object
      const partAnswers = reduce(
        get(subscrAnswers, 'parts', []),
        (acc, part) => {
          const groupedAnswers = groupBy(part.answers, 'path.root');

          forEach(groupedAnswers, (groupAnswer, key) => {
            if (!acc[key]) acc[key] = {};
            acc[key] = { ...acc[key], ...getAnswersWithPath(groupAnswer) };
          });

          return acc;
        },
        {}
      );

      return {
        ...partAnswers,
        ref: key.replace(/_block/gi, ''),
      };
    });

    let newParties = map(get(salesroute, 'parties', []), partie => {
      const upPartie = find(subscrData, { ref: partie.ref });
      const newPartie = omit(partie, ['bank_accounts']);

      if (upPartie) {
        // Do not save addresses with only country (wich is set by default)
        if (
          upPartie?.parties?.addresses?.[0] &&
          size(upPartie.parties.addresses[0]) === 1 &&
          upPartie.parties.addresses[0].country
        ) {
          upPartie.parties.addresses = [];
        }

        return { ...newPartie, ...upPartie.parties };
      }

      return newPartie;
    });

    const newContracts = map(get(salesroute, 'contracts', []), contract => {
      const upPartie = find(subscrData, { ref: contract.subscriber.ref });
      const partie = find(newParties, { ref: contract.subscriber.ref });

      // Remove Personne Morale si il y en a (passage de PM à PP après sauvegarde par exemple)
      if (partie.is_person) {
        newParties = filter(newParties, p => p.is_person);
      }

      if (upPartie) {
        if (upPartie.contract?.billing?.billing_mode?.id != null) {
          upPartie.contract.billing.billing_mode.id = parseInt(upPartie.contract.billing.billing_mode.id, 10);
        }

        if (upPartie.contract?.billing?.direct_debit_day != null) {
          upPartie.contract.billing.direct_debit_day = parseInt(upPartie.contract.billing.direct_debit_day, 10);
        }

        return { ...contract, ...upPartie.contract };
      }

      return { ...contract };
    });

    return {
      contracts: newContracts,
      parties: newParties,
      quotations: salesroute?.quotations,
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`partialUpdateSalesRouteSubscriptionTransform ${e.stack}`);
  }
  return undefined;
};

const getAnswer = (question, locale) => {
  let obj;
  let val;

  if (question.value != null) {
    val = question.value;
  } else if (question.init != null) {
    val = question.init;
  } else if (question.default != null) {
    val = question.default;
  }

  const escapeMarkdown = name => {
    if (name === '-') return '\\-';
    return name != null ? `${name}` : name;
  };

  switch (question.type) {
    case 'selection':
      obj = find(question.selection, { value: val });
      return obj ? escapeMarkdown(obj.name) : val;
    case 'birth_date':
    case 'date':
      return formatDate({ date: val, dateFormat: 'D MMM YYYY', lang: locale });
    case 'currency':
      return val != null ? formatAmountCurrency(val, locale) : val;
    case 'percentage':
      return val != null ? formatPercent(val / 100, locale) : val;
    default:
      return escapeMarkdown(val);
  }
};

/**
 *  check Conditions By Values
 * @param {*} param0
 */
const checkConditionsByValues = ({ conditions, values }) => {
  try {
    // if array conditions is empty then return true
    if (size(conditions) <= 0) return true;

    // calculate result conditions
    const resultConditions = Object.keys(conditions).reduce((result, key) => {
      const valueCondition = get(conditions[key], 'value');
      const valueField = get(values, get(conditions[key], 'code'));
      const operatorCondition = get(conditions[key], 'operator');
      let resultConditionItem;
      //
      switch (operatorCondition) {
        case '=':
          resultConditionItem = valueField === valueCondition;
          break;
        case '>':
          resultConditionItem = valueField > valueCondition;
          break;
        case '<':
          resultConditionItem = valueField < valueCondition;
          break;
        case '>=':
          resultConditionItem = valueField >= valueCondition;
          break;
        case '<=':
          resultConditionItem = valueField <= valueCondition;
          break;
        case '!=':
          resultConditionItem = valueField !== valueCondition;
          break;
        default:
          resultConditionItem = valueField === valueCondition;
          break;
      }
      // by default the logical operator `&&` is applied for concatenated all conditions
      return result && resultConditionItem;
    }, true);
    return resultConditions;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`checkConditionsByValues ${e.stack}`);
  }
  return undefined;
};

const getValues = questionsFull => {
  const values = {};

  forEach(questionsFull, q => {
    if (q.value != null) {
      values[q.code] = q.value;
    } else if (q.init != null) {
      values[q.code] = q.init;
    } else if (q.default != null) {
      values[q.code] = q.default;
    }

    // Force convert number to string for selection field because Formik transform it to string...
    if (q.type === 'selection' && isNumber(values[q.code])) {
      values[q.code] = `${values[q.code]}`;
    }
  });

  return values;
};

/**
 * Get graduated loan summary
 * ex: Palier n°1 : 500,00 € sur 12 Mois
 */
const transformToGraduatedSummary = (blocks = [], locale, strings) => {
  const colonFormat = locale === 'fr' ? ' :' : ':';
  const summaryGraduated = [];

  forEach(blocks, (block, idxBlock) => {
    const summaryQuestion = {
      id: `${block.code}_${idxBlock}`,
      question: block.displayTitle ? `${get(block, 'title', '')}${colonFormat}` : '',
      answer: '',
    };

    forEach(sortBy(get(block, 'parts', []), ['sequence']), part => {
      const questionsFull = get(part, 'questions', []);
      const values = getValues(questionsFull);

      forEach(sortBy(get(part, 'groups', [{ questions: questionsFull }]), ['sequence']), (group, idx) => {
        let answer = get(
          strings,
          'editsalesroute.loan.graduated.template.summary',
          '__payment_amount__ sur __number_of_payments__ __payment_frequency__'
        );

        forEach(sortBy(get(group, 'questions', []), ['sequence']), question => {
          const q = find(questionsFull, { code: question.code });
          const resultShow = checkConditionsByValues({ conditions: q.conditions, values });

          if (resultShow) {
            answer = answer.replace(new RegExp(`__${q.code}__`), getAnswer(q, locale));
          }
        });

        summaryQuestion.answer += answer;
      });

      summaryGraduated.push(summaryQuestion);
    });
  });

  return { id: 'graduated_loan', title: '', questions: summaryGraduated };
};

const getRole = options => {
  try {
    const optionsParse = JSON.parse(options);
    const role = get(optionsParse, 'role');
    return role;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`parseToJson ${e.stack}`);
  }
  return null;
};

export const transformToSummaryGrouped = (blocks = [], locale, strings, graduatedLoans) => {
  const colonFormat = locale === 'fr' ? ' :' : ':';
  const summaryGroups = [];

  forEach(blocks, (block, idxBlock) => {
    const summaryBigGroup = {
      sequence: idxBlock,
      title: block.displayTitle
        ? getNewTitle({
            title: getLabel({ label: block?.title, strings, defaultString: '' }),
            isTitleMultiple: block?.isTitleMultiple,
            idx: idxBlock + 1,
          })
        : '',
      questions: [],
    };

    forEach(sortBy(get(block, 'parts', []), ['sequence']), part => {
      const questionsFull = get(part, 'questions', []);
      let isGraduatedLoan = false;

      const values = getValues(questionsFull);

      forEach(sortBy(get(part, 'groups', [{ questions: questionsFull }]), ['sequence']), (group, idx) => {
        const questions = reduce(
          sortBy(get(group, 'questions', []), ['sequence']),
          (result, question, idxQ) => {
            const q = find(questionsFull, { code: question.code });
            const role = getRole(get(q, 'custom_resources.options', '{}'));
            const hidden = role === 'hidden';

            if (!hidden) {
              const resultShow = checkConditionsByValues({ conditions: q.conditions, values });

              if (resultShow) {
                result.push({
                  id: `${block.id}_${part.id}_${idx + 1}_${idxQ + 1}`,
                  question: `${q.label || q.name}${colonFormat}`,
                  answer: getAnswer(q, locale),
                });

                if (q.code === 'loan_kind' && q.value === 'graduated') {
                  isGraduatedLoan = true;
                }
              }
            }

            return result;
          },
          []
        );

        const groupTitle = block.displayTitle ? getLabel({ label: group?.title, strings, defaultString: '' }) : '';

        const summGroup = { id: `${block.id}_${part.id}_${idx + 1}`, title: groupTitle, questions };

        summaryBigGroup.questions.push(summGroup);
      });

      // Graduated Loans
      if (isGraduatedLoan && graduatedLoans && graduatedLoans[block.id]) {
        let graduatedQuestions = transformToGraduatedSummary(graduatedLoans[block.id], locale, strings);

        summaryBigGroup.questions = [...summaryBigGroup.questions, graduatedQuestions];
      }
    });

    if (get(block, 'parts', []).length) {
      summaryGroups.push(summaryBigGroup);
    }
  });

  return summaryGroups;
};

export const transformToQuestionnaireSummary = (block, locale, strings, idxLoan) => {
  const colonFormat = locale === 'fr' ? ' :' : ':';
  const summary = [];

  forEach(sortBy(get(block, 'parts', []), ['sequence']), part => {
    const questionsFull = get(part, 'questions', []);
    const values = {};

    forEach(questionsFull, q => {
      values[q.code] = q.value || q.init || q.default;
    });

    forEach(sortBy(get(part, 'groups', [{ questions: questionsFull }]), ['sequence']), (group, idx) => {
      const questions = reduce(
        sortBy(get(group, 'questions', []), ['sequence']),
        (result, question, idxQ) => {
          const q = find(questionsFull, { code: question.code });
          const resultShow = checkConditionsByValues({ conditions: q.conditions, values });

          if (resultShow) {
            result.push({
              id: `${idx + 1}${idxQ + 1}`,
              question: `${q.label || q.name}${colonFormat}`,
              answer: getAnswer(q, locale),
            });
          }

          return result;
        },
        []
      );

      let groupTitle = '';

      if (block.displayTitle) {
        groupTitle += getLabel({ label: block?.title, strings, defaultString: '' });
      }
      if (group?.title) {
        if (groupTitle.length) groupTitle += ' ';
        groupTitle += getLabel({ label: group?.title, strings, defaultString: '' });
      }

      const summGroup = { id: `${block.id}${idx + 1}`, title: groupTitle, questions };

      summary.push(summGroup);
    });
  });

  return summary;
};

export const transformToLoansSummary = (block, locale, strings) => {
  const summary = transformToSummaryGrouped(get(block, 'loans', []), locale, strings, get(block, 'graduatedLoans', {}));

  return summary;
};

export const transformToExtraDataSummary = (block, locale, strings) => {
  return transformToQuestionnaireSummary(block, locale, strings);
};

export const transformToGeneralDataSummary = (block, locale, strings) => {
  return transformToQuestionnaireSummary(block, locale, strings);
};

export const transformToQuotationSummary = (block, locale, strings) => {
  const summary = transformToSummaryGrouped(get(block, 'quotations', []), locale, strings);

  return summary;
};

export const transformToOfferSummary = (block = [], locale, strings) => {
  const colonFormat = locale === 'fr' ? ' :' : ':';
  const question = `${get(strings, 'salesroute.offer.selected_formula', 'Formule sélectionnée')}${colonFormat}`;

  const summary = map(
    block,
    aBlock => {
      const resp = {
        id: `${get(aBlock, 'id')}`,
        title: get(aBlock, 'offer.dataOffer.extraData.title', ''),
        questions: [
          {
            id: `${get(aBlock, 'id')}1`,
            question,
            answer: 'Aucune',
          },
        ],
      };
      const formulaSelected = get(aBlock, 'offer.formulaSelected');

      if (formulaSelected) {
        const formula = find(get(aBlock, 'offer.dataOffer.formulas', []), { code: formulaSelected });

        if (formula) {
          const covereds = map(get(aBlock, 'offer.dataOffer.data', []), (data, idx) => {
            const pkg = find(get(data, 'packages', []), { ref: formulaSelected });

            return {
              id: `${get(aBlock, 'id')}${idx + 2}`,
              question: `${data.label}${colonFormat}`,
              answer: pkg?.value ?? '',
            };
          });

          resp.questions = [
            {
              id: `${get(aBlock, 'id')}1`,
              question,
              answer: formula.value,
            },
            ...covereds,
          ];
        }
      }

      return resp;
    },
    []
  );

  return summary;
};

export const transformToSubscriptionSummary = (block = [], locale, strings) => {
  const summary = transformToSummaryGrouped(get(block, 'subscribers', []), locale, strings);

  return summary;
};

export const transformToDocumentsSummary = (block = [], locale, strings) => {
  // TODO when print is working
  const summary = [];

  forEach(get(block, 'documents', []), (doc, idx) => {
    let questions = [];

    questions = map(get(block, 'contracts', []), (contract, idxC) => ({
      id: `${idx + 1}${idxC + 1}`,
      question: contract.label,
      answer: '',
    }));

    questions.push({
      id: `${idx + (get(block, 'contracts', []).length + 1)}2`,
      question: '',
      answer: '',
    });

    const summGroup = {
      id: `${idx + 1}`,
      title: '',
      questions,
    };

    summary.push(summGroup);
  });

  return summary;
};
